import Grid from '@material-ui/core/Grid';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Icon from 'react-eva-icons';

import info from '@coursera/assets/icons/info.svg';
import x from '@coursera/assets/icons/x-no-circle.svg';
import { Button, MaterialModal, Instructions, Coachmark } from '@coursera/components';

import styles from './risk-matrix.module.scss';

const cx = classnames.bind(styles);

const matrixLocations = [
  ['low-impact', 'high-probability'],
  ['medium-impact', 'high-probability'],
  ['high-impact', 'high-probability'],
  ['low-impact', 'medium-probability'],
  ['medium-impact', 'medium-probability'],
  ['high-impact', 'medium-probability'],
  ['low-impact', 'low-probability'],
  ['medium-impact', 'low-probability'],
  ['high-impact', 'low-probability'],
];

function GridItem({
  item,
  impact,
  probability,
  addItem,
  removeItem,
  expandItem,
  expanded,
  modalBackground,
}) {
  const [modalOpen, setModalOpen] = useState(false);

  if (item === undefined) {
    return (
      <div onClick={addItem} onKeyPress={addItem} tabIndex={0}>
        <div className={styles.icon}>
          <Icon size="large" name={'plus-outline'} />
        </div>
        <div className={styles.showOnDesktop}>{impact} impact,</div>
        <div className={styles.showOnDesktop}>{probability} probability</div>
      </div>
    );
  }

  const expandButtonIconCss = cx({
    expandButtonIcon: true,
    expanded: expanded,
  });

  const cardDescriptionCss = cx({
    cardDescriptionCollapsed: !expanded,
    cardDescriptionExpanded: expanded,
  });

  const modalSeverityCss = cx({
    modalSeverity: true,
    [modalBackground]: true,
  });

  return (
    <>
      <div className={`${styles.cardItem} ${styles.showOnDesktop}`}>
        <span className={styles.cardButtonRow}>
          <Button onClick={removeItem} variant="text">
            <img className={styles.removeButtonIcon} src={x} /> Remove
          </Button>
          <Button onClick={expandItem} variant="text">
            {expanded ? 'Read less ' : 'Read more '}
            <span className={expandButtonIconCss}>
              <Icon size="medium" name={'arrow-ios-downward-outline'} fill="#979797" />
            </span>
          </Button>
        </span>
        <div className={cardDescriptionCss}>
          <div className={styles.nextTaskBold}>{item.title}</div>
          <p>{item.description}</p>
          {item.description2 && <p>{item.description2}</p>}
        </div>
      </div>

      <div className={styles.showOnMobile}>
        <div className={styles.icon} onClick={() => setModalOpen(true)}>
          <img className={styles.infoIcon} src={info} />
        </div>
      </div>

      <MaterialModal
        open={modalOpen}
        appElementId="root"
        ctaText="Got it"
        closeModal={() => setModalOpen(false)}
        requestClose={() => setModalOpen(false)}
      >
        <>
          <div className={styles.modalRemove}>
            <Button
              onClick={() => {
                removeItem();
                setModalOpen(false);
              }}
              variant="text"
            >
              <img className={styles.removeButtonIcon} src={x} /> Remove from matrix
            </Button>
          </div>
          <div className={modalSeverityCss}>
            {impact} impact, {probability} probability
          </div>
          <div className={styles.cardDescriptionExpanded}>
            <div className={styles.nextTaskBold}>{item.title}</div>
            <p>{item.description}</p>
            {item.description2 && <p>{item.description2}</p>}
          </div>
        </>
      </MaterialModal>
    </>
  );
}

GridItem.propTypes = {
  item: PropTypes.any,
  impact: PropTypes.string,
  probability: PropTypes.string,
  addItem: PropTypes.func,
  removeItem: PropTypes.func,
  expandItem: PropTypes.func,
  expanded: PropTypes.bool,
  modalBackground: PropTypes.string,
};

function RiskMatrix({ items, xLabel, yLabel, onComplete, onProgress, back }) {
  const [itemsState, setItemsState] = useState(items);
  const [rowOneExpanded, setRowOneExpanded] = useState(false);
  const [rowTwoExpanded, setRowTwoExpanded] = useState(false);
  const [rowThreeExpanded, setRowThreeExpanded] = useState(false);
  const count = itemsState.length;

  const getNextAvailable = () => itemsState.find((item) => item.answer === undefined);

  const itemForLocation = (locationId) => {
    return itemsState.find((item) => item.answer === locationId);
  };

  const nextTask = getNextAvailable();
  const currentIndex = itemsState.indexOf(nextTask) + 1;
  const unassignedItems = itemsState.filter((item) => item.answer !== undefined).length + 1;
  const counter = `${currentIndex === 0 ? count : unassignedItems} of ${count}`;
  const isCompleted = nextTask === undefined;

  const addNextItemToLocation = (location) => {
    const item = nextTask;
    if (item === undefined) return;

    let percent = 0;

    const updatedItemList = itemsState.map((e) => {
      const output = { ...e };

      if (e.id === item.id) {
        output.answer = location;
      }

      if (output.answer !== undefined) {
        percent += 1 / count;
      }

      return output;
    });

    setItemsState(updatedItemList);

    if (onProgress) {
      onProgress(percent);
    }

    if (percent >= 1) {
      onComplete(updatedItemList);
    }
  };

  const removeFromLocation = (location) => {
    const item = itemForLocation(location);
    if (item === undefined) return;

    let percent = 0;

    const updatedItemList = itemsState.map((e) => {
      const output = { ...e };

      if (e.id === item.id) {
        output.answer = undefined;
      }

      if (output.answer !== undefined) {
        percent += 1 / count;
      }

      return output;
    });

    setItemsState(updatedItemList);

    if (onProgress) {
      onProgress(percent);
    }
  };

  const expandRow = (row) => {
    switch (row) {
      case 1:
        setRowOneExpanded(!rowOneExpanded);
        break;
      case 2:
        setRowTwoExpanded(!rowTwoExpanded);
        break;
      case 3:
        setRowThreeExpanded(!rowThreeExpanded);
        break;
      default:
        break;
    }
  };

  const allExpanded = rowOneExpanded && rowTwoExpanded && rowThreeExpanded;

  const expandAll = () => {
    setRowOneExpanded(!allExpanded);
    setRowTwoExpanded(!allExpanded);
    setRowThreeExpanded(!allExpanded);
  };

  return (
    <>
      <div className={styles.riskMatrixContainer}>
        <Grid container spacing={1}>
          <Grid item md={4}>
            <div className={styles.showOnDesktop}>
              <Instructions
                title="Complete the risk matrix"
                desc="Select the area in the matrix where the scenario belongs."
              />
            </div>
            {nextTask && (
              <div className={styles.showOnMobile}>
                <Instructions mTitle="Tap the impact/probability type for the given scenario:" />
              </div>
            )}
          </Grid>
          <Grid item md={8}>
            {nextTask !== undefined ? (
              <div className={styles.nextTask}>
                <p className={styles.nextTaskBold}>{counter}</p>
                <p className={styles.nextTaskBold}>{nextTask.title}</p>
                <p>{nextTask.description}</p>
                {nextTask.description2 && <p>{nextTask.description2}</p>}
              </div>
            ) : (
              <p className={styles.completeText}>You&apos;ve categorized everything.</p>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={1} className={styles.riskMatrix}>
          <Grid item xs={2} />
          <Grid container item xs={10} spacing={2}>
            <Grid item xs={4} />
            <Grid item xs={4} className={styles.gridHeading}>
              {xLabel}
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={2}>
              <div className={styles.showOnDesktop}>
                <Button onClick={expandAll} variant="text" disabled={unassignedItems === 1}>
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </Button>
              </div>
            </Grid>
          </Grid>

          <Grid item xs={2} />
          <Grid container item xs={10} spacing={2}>
            <Grid item xs={4}>
              <div className={`${styles.rowHeading} ${styles.low}`}>Low</div>
            </Grid>
            <Grid item xs={4}>
              <div className={`${styles.rowHeading} ${styles.medium}`}>Medium</div>
            </Grid>
            <Grid item xs={4}>
              <div className={`${styles.rowHeading} ${styles.high}`}>High</div>
            </Grid>
          </Grid>

          <Grid item xs={1} />
          <Grid item xs={1}>
            <div className={`${styles.yLabelContainer} ${styles.high}`}>
              <span className={`${styles.yLabel} ${styles.rowHeading}`}>High</span>
            </div>
          </Grid>
          <Grid container item xs={10} spacing={2}>
            <Grid item xs={4}>
              <div className={`${styles.mediumItem} ${rowOneExpanded && styles['item-expanded']}`}>
                <GridItem
                  item={itemForLocation(matrixLocations[0])}
                  impact="Low"
                  probability="high"
                  addItem={() => addNextItemToLocation(matrixLocations[0])}
                  removeItem={() => removeFromLocation(matrixLocations[0])}
                  expandItem={() => expandRow(1)}
                  expanded={rowOneExpanded}
                  modalBackground="medium"
                />
              </div>
            </Grid>
            <Grid item xs={4}>
              <div className={`${styles.highItem} ${rowOneExpanded && styles['item-expanded']}`}>
                <GridItem
                  item={itemForLocation(matrixLocations[1])}
                  impact="Medium"
                  probability="high"
                  addItem={() => addNextItemToLocation(matrixLocations[1])}
                  removeItem={() => removeFromLocation(matrixLocations[1])}
                  expandItem={() => expandRow(1)}
                  expanded={rowOneExpanded}
                  modalBackground="high"
                />
              </div>
            </Grid>
            <Grid item xs={4}>
              <div className={`${styles.highItem} ${rowOneExpanded && styles['item-expanded']}`}>
                <GridItem
                  item={itemForLocation(matrixLocations[2])}
                  impact="High"
                  probability="high"
                  addItem={() => addNextItemToLocation(matrixLocations[2])}
                  removeItem={() => removeFromLocation(matrixLocations[2])}
                  expandItem={() => expandRow(1)}
                  expanded={rowOneExpanded}
                  modalBackground="high"
                />
              </div>
            </Grid>
          </Grid>

          <Grid item align="center" justify="center" xs={1}>
            <div className={styles.yLabelContainer}>
              <span className={`${styles.yLabelHeading} ${styles.gridHeading}`}>{yLabel}</span>
            </div>
          </Grid>
          <Grid item xs={1}>
            <div className={`${styles.yLabelContainer} ${styles.medium}`}>
              <span className={`${styles.yLabel} ${styles.rowHeading}`}>Medium</span>
            </div>
          </Grid>
          <Grid container item xs={10} spacing={2}>
            <Grid item xs={4}>
              <div className={`${styles.lowItem} ${rowTwoExpanded && styles['item-expanded']}`}>
                <GridItem
                  item={itemForLocation(matrixLocations[3])}
                  impact="Low"
                  probability="medium"
                  addItem={() => addNextItemToLocation(matrixLocations[3])}
                  removeItem={() => removeFromLocation(matrixLocations[3])}
                  expandItem={() => expandRow(2)}
                  expanded={rowTwoExpanded}
                  modalBackground="low"
                />
              </div>
            </Grid>
            <Grid item xs={4}>
              <div className={`${styles.mediumItem} ${rowTwoExpanded && styles['item-expanded']}`}>
                <GridItem
                  item={itemForLocation(matrixLocations[4])}
                  impact="Medium"
                  probability="medium"
                  addItem={() => addNextItemToLocation(matrixLocations[4])}
                  removeItem={() => removeFromLocation(matrixLocations[4])}
                  expandItem={() => expandRow(2)}
                  expanded={rowTwoExpanded}
                  modalBackground="medium"
                />
              </div>
            </Grid>
            <Grid item xs={4}>
              <div className={`${styles.highItem} ${rowTwoExpanded && styles['item-expanded']}`}>
                <GridItem
                  item={itemForLocation(matrixLocations[5])}
                  impact="High"
                  probability="medium"
                  addItem={() => addNextItemToLocation(matrixLocations[5])}
                  removeItem={() => removeFromLocation(matrixLocations[5])}
                  expandItem={() => expandRow(2)}
                  expanded={rowTwoExpanded}
                  modalBackground="high"
                />
              </div>
            </Grid>
          </Grid>

          <Grid item xs={1} />
          <Grid item xs={1}>
            <div className={`${styles.yLabelContainer} ${styles.low}`}>
              <span className={`${styles.yLabel} ${styles.rowHeading}`}>Low</span>
            </div>
          </Grid>
          <Grid container item xs={10} spacing={2}>
            <Grid item xs={4}>
              <div className={`${styles.lowItem} ${rowThreeExpanded && styles['item-expanded']}`}>
                <GridItem
                  item={itemForLocation(matrixLocations[6])}
                  impact="Low"
                  probability="low"
                  addItem={() => addNextItemToLocation(matrixLocations[6])}
                  removeItem={() => removeFromLocation(matrixLocations[6])}
                  expandItem={() => expandRow(3)}
                  expanded={rowThreeExpanded}
                  modalBackground="low"
                />
              </div>
            </Grid>
            <Grid item xs={4}>
              <div className={`${styles.lowItem} ${rowThreeExpanded && styles['item-expanded']}`}>
                <GridItem
                  item={itemForLocation(matrixLocations[7])}
                  impact="Medium"
                  probability="low"
                  addItem={() => addNextItemToLocation(matrixLocations[7])}
                  removeItem={() => removeFromLocation(matrixLocations[7])}
                  expandItem={() => expandRow(3)}
                  expanded={rowThreeExpanded}
                  modalBackground="low"
                />
              </div>
            </Grid>
            <Grid item xs={4}>
              <div
                className={`${styles.mediumItem} ${rowThreeExpanded && styles['item-expanded']}`}
              >
                <GridItem
                  item={itemForLocation(matrixLocations[8])}
                  impact="High"
                  probability="low"
                  addItem={() => addNextItemToLocation(matrixLocations[8])}
                  removeItem={() => removeFromLocation(matrixLocations[8])}
                  expandItem={() => expandRow(3)}
                  expanded={rowThreeExpanded}
                  modalBackground="medium"
                />
              </div>
            </Grid>
          </Grid>
        </Grid>
      </div>

      <Button.Container fixed hidden={!isCompleted}>
        <Button onClick={back} variant="outline">
          Go back
        </Button>
      </Button.Container>

      <div hidden={!isCompleted}>
        <Coachmark
          position="right"
          showArrow={false}
          label="To see results, accept the honor code and submit your answers below. Close full screen first if using."
        />
      </div>
    </>
  );
}

RiskMatrix.propTypes = {
  xLabel: PropTypes.string,
  yLabel: PropTypes.string,
  items: PropTypes.array,
  onComplete: PropTypes.func,
  onProgress: PropTypes.func,
  back: PropTypes.func,
};

export default RiskMatrix;
